import AnimationController from '../../assets/js/controllers/animation';
import CursorController from '../../assets/js/controllers/cursor';
import ResizeController from '../../assets/js/controllers/resize';

// import EaseBezier from '../ease-bezier/script';

import Platform from '../../assets/js/constants/platform';
import SliderDraggableEvent from './slider-draggable-event';

import { clamp, getRootOffsetBounds, multiClassList, validateParamBoolean } from '../../assets/js/helpers';

/*

*********************************
Elements:

[data-slider-draggable] 				: the dom element representing the masked wrapper of the slider.
[data-slider-draggable-items]		 	: the dom element containing slider items.
[data-slider-draggable-item] 			: (optional) a slider item. using this will disable automatically looking for items as direct descendents of [slider-draggable-items]

*********************************
Event Controls:

SliderDraggableEvent.SEEK 				: change slider item to index
SliderDraggableEvent.NEXT				: change slider item to the next index (default: will loop)
SliderDraggableEvent.PREV				: change slider item to the previous index (default: will loop)

*********************************
Options:

[data-slider-draggable-dr]				: (config.snapping.itemSnapRatio) default: 0.33
[data-slider-draggable-gl]				: (config.snapping.left) default: 100
[data-slider-draggable-gr]				: (config.snapping.right) default: 100

[data-slider-draggable-mobile]			: (config.isMobileEnabled) default: false
										  assumed true if parameter is included but empty

[data-slider-draggable-full-width]		: (config.isFullWidth) default: false
								  		assumed true if parameter is included but empty
								  		1) true: each slider item occupies the full width of its container. the slider will also loop when the user selects an index below 0 or greater than the total limit.
							 	  		2) false: multiple items may be on screen at the same time. the slider will have a set beginning and end (NOT infinite looping)

*********************************
Quick Start:

1. add `data-slider-draggable` to the outermost wrapper of anything using the slider
2. add `data-slider-items` to the closest wrapper of all sliding items
3. (optional) add `data-slider-item` to each sliding item within the `data-slider-items` wrapper

CSS requirements:

styles will be automatically applied if:
1. you add the 'slider-draggable' class to the SAME element as `data-slider-draggable`
2. you add the 'slider-draggable__items-wrapper' class to the PARENT element of `data-slider-draggable-items`

*********************************

*/

export default class SliderDraggable {

	static selector = '[data-slider-draggable]';

	// class variables

	// child params (can be altered by optional data attributes)

	defaults = {
		bezier: [0.000, 0.000, 0.002, 1.000],
		delays: {
			dragStart: 160
		},
		flicking: {
			lerp: {
				ratio: 0.12,
				roundOff: 0.1
			}
		},
		isFullWidth: false,
		isMobileEnabled: false,
		isInfinite: false,
		snapping: {
			itemSnapRatio: 0.25, // ratio of item width that needs to be dragged in order to snap to the next or previous item
			left: 100,
			right: 100,
			lerp: {
				ratio: 0.12,
				roundOff: 0.1
			}
		}
	};

	config = {
		isFullWidth: false // full width mode does not allow flick scrolling
	};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		cursor: {
			last: {
				x: 0,
				lerped: {
					x: 0
				}
			},
			position: {
				x: 0,
				lerped: {
					x: 0
				}

			},
			start: {
				x: 0,
				lerped: {
					x: 0
				}
			}
		},
		flick: {
			vx: 0
		},
		slides: {
			boundaries: {
				top: 0,
				bottom: 0,
				left: 0,
				right: 0
			},
			destination: {
				x: 0
			},
			position: {
				x: 0,
				lerped: {
					dx: 0,	// velocity x
					x: 0	// position x
				}
			},
			start: {
				x: 0,
				lerped: {
					x: 0
				}
			}
		},
		container: null,
		itemsWrapper: null
	};

	el = {};

	flags = {
		// status
		isEnabled: false,

		// mouse position
		isMouseOver: false,

		// lerped cursor position
		isLerpOver: false,

		// slider
		isDragging: false,
		isDragComplete: true,

		// seeking behavior
		isSeeking: false,

		// flicking behavior
		isFlickCompletePending: false
	};

	states = {
		// progress
		index: 0,
		total: 0,
		ratio: 0
	};

	timeouts = {
		anchorDown: null,
		drag: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			items: container.querySelectorAll('[data-slider-draggable-item]'),
			itemInnerWrappers: container.querySelectorAll('[data-slider-draggable-item-inner-wrapper]'), // optional
			itemsWrapper: container.querySelector('[data-slider-draggable-items]'),
		};

		// fallback if [data-slider-draggable-item] is not defined
		if (!this.el.items.length) {
			this.el.items = this.el.itemsWrapper.children;
		}

		this.states.total = this.el.items.length;


		// dom attributes (overriding config)
		this.config = { ...this.defaults };
		this.config.isFullWidth = validateParamBoolean(this.el.container, 'data-slider-draggable-full-width', this.config.isFullWidth, true);
		this.config.isMobileEnabled = validateParamBoolean(this.el.container, 'data-slider-draggable-mobile', this.config.isMobileEnabled, true);
		this.config.snapping.left = this.el.container.dataset.sliderDraggableGl || this.config.snapping.left;
		this.config.snapping.right = this.el.container.dataset.sliderDraggableGr || this.config.snapping.right;
		this.config.isInfinite = validateParamBoolean(this.el.container, 'data-slider-draggable-infinite', this.config.isInfinite, true);

		// if (this.config.isInfinite) {
		// 	const firstItem = this.el.items[0].cloneNode(true);
		// 	const lastItem = this.el.items[this.states.total - 1].cloneNode(true);
		// 	// const newItems = lastItem + this.el.items + firstItem;
		// 	console.log('test', firstItem, lastItem, this.el.items);

		// 	this.el.items[0].before(lastItem);
		// 	this.el.items[this.states.total - 1].after(lastItem);

		// 	this.el.items = this.el.itemsWrapper.children;
		// 	this.states.total = this.el.items.length;
		// }

		this.initialize();
	}

	// init

	initialize() {
		this.#addEventListeners();
	}

	//

	#addEventListeners = () => {
		this.el.container.addEventListener(SliderDraggableEvent.NEXT, this.#onNext);
		this.el.container.addEventListener(SliderDraggableEvent.PREV, this.#onPrev);
		this.el.container.addEventListener(SliderDraggableEvent.SEEK, this.#onSeek);

		this.el.container.addEventListener('mousedown', this.#onMouseDown);
		this.el.container.addEventListener('touchstart', this.#onTouchStart);
		this.el.container.addEventListener('touchend', this.#onTouchEnd);
		this.el.container.addEventListener('touchcancel', this.#onTouchCancel);

		document.addEventListener('mouseleave', this.#onDocumentMouseLeave);

		// need to cancel default anchor events when being dragged

		if (this.el.anchors && this.el.anchors.length) {
			for (const anchor of this.el.anchors) {
				anchor.addEventListener('dragstart', this.#onAnchorDrag);
				anchor.addEventListener('mousedown', this.#onAnchorMouseDown);
				anchor.addEventListener('click', this.#onAnchorClick);
			}
		}

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.tick();
	}

	#removeEventListeners = () => {
		this.el.container.removeEventListener(SliderDraggableEvent.NEXT, this.#onNext);
		this.el.container.removeEventListener(SliderDraggableEvent.PREV, this.#onPrev);
		this.el.container.removeEventListener(SliderDraggableEvent.SEEK, this.#onSeek);

		this.el.container.removeEventListener('mousedown', this.#onMouseDown);
		this.el.container.removeEventListener('touchstart', this.#onTouchStart);
		this.el.container.removeEventListener('touchend', this.#onTouchEnd);
		this.el.container.removeEventListener('touchcancel', this.#onTouchCancel);

		document.removeEventListener('mouseup', this.#onMouseUp);
		document.removeEventListener('mouseleave', this.#onDocumentMouseLeave);

		if (this.el.anchors && this.el.anchors.length) {
			for (const anchor of this.el.anchors) {
				anchor.removeEventListener('dragstart', this.#onAnchorDrag);
				anchor.removeEventListener('mousedown', this.#onAnchorMouseDown);
				anchor.removeEventListener('click', this.#onAnchorClick);
			}
		}

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);

		this.controllers.animation = null;
		this.controllers.resize = null;
	}

	// listener methods

	#onNext = () => {
		this.seek(this.states.index + 1);
	}

	#onPrev = () => {
		this.seek(this.states.index - 1);
	}

	#onSeek = (e) => {
		this.seek(e.detail.index);
	}

	#onMouseDown = (e) => {
		if (this.flags.isEnabled) {
			e.preventDefault();
		}

		this.flags.isMouseDown = true;
		document.removeEventListener('mouseup', this.#onMouseUp);
		document.addEventListener('mouseup', this.#onMouseUp);
		this.checkMouseDown();
	}

	#onTouchStart = () => {
		if (this.flags.isEnabled) {
			document.removeEventListener('select', this.#onSelect);
			document.addEventListener('select', this.#onSelect);
		}

		this.flags.isMouseDown = true;
		this.checkMouseDown();
	}

	#onTouchEnd = () => {
		document.removeEventListener('select', this.#onSelect);
		this.flags.isMouseDown = false;
		this.checkMouseUp();
	}

	#onTouchCancel = () => {
		this.flags.isMouseDown = false;
		this.checkMouseUp();
	}

	#onSelect = (e) => {
		e.preventDefault();
	}

	#onMouseUp = () => {
		this.flags.isMouseDown = false;
		this.checkMouseUp();
	}

	#onDocumentMouseLeave = () => {
		this.flags.isMouseDown = false;
		this.checkMouseUp();
	}

	#onAnchorDrag = (e) => {
		e.preventDefault();
	}

	#onAnchorMouseDown = () => {
		// if (this.timeouts.anchorDown) { clearTimeout(this.timeouts.anchorDown); }
		// this.timeouts.anchorDown = setTimeout(() => {
		// 	this.flags.isMouseDown = true;
		// 	this.el.container.classList.add('is-disabled');
		// }, 250);
	}

	#onAnchorClick = (e) => {
		if (this.flags.isMouseDown) {
			this.flags.isMouseDown = false;
			e.stopPropagation();
			e.preventDefault();
		}
	}

	// controller methods

	#onAnimationUpdate = () => this.measureDrag();
	#onAnimationRender = () => this.renderDrag();

	#onResizeUpdate = () => this.measureResize();
	#onResizeRender = () => this.renderResize();

	// seek to index

	seek(index, instant = false) {
		if (!isNaN(parseInt(index))) {

			let clampedIndex = clamp(index, 0, this.states.total - 1);

			// Allow infinite slides
			if (this.config.isInfinite) {
				if (index <= -1) {
					clampedIndex = this.states.total - 1;
					// instant = true;
				}

				if (index >= this.states.total) {
					clampedIndex = 0;
					// instant = true;
				}
			}

			const itemWidth = this.el.items[0].scrollWidth;

			this.coords.slides.destination.x = 0 - clampedIndex * itemWidth;

			if (instant) {
				this.coords.slides.position.x = this.coords.slides.destination.x;
			}

			this.coords.slides.position.lerped.dx = 0;
			this.coords.slides.position.lerped.x = this.coords.slides.position.x;

			// set index

			this.states.index = clampedIndex;

			// set class on active item

			multiClassList(this.el.items, 'remove', 'is-active');
			this.el.items[this.states.index].classList.add('is-active');

			// release dragging

			this.flags.isDragging = false;
			this.flags.isDragComplete = false;
			this.flags.isSeeking = true;

			// dispatch event

			const detail = {
				index: this.states.index
			};

			this.el.container.dispatchEvent(new CustomEvent(SliderDraggableEvent.SEEKING, { detail }));
		}
	}

	// flick

	flick(vx) {
		if (this.flags.isEnabled) {
			this.coords.flick.vx = vx;
			this.flags.isDragging = false;
			this.flags.isDragComplete = false;
			this.flags.isFlickComplete = false;
			this.flags.isFlickCompletePending = false;
		}
	}

	flickComplete() {
		this.flags.isFlickComplete = true;
		this.flags.isFlickCompletePending = false;

		let distanceClosest = -1;
		let indexClosest = -1;
		let $itemClosest = null;

		for (let i = 0; i < this.el.items.length; i++) {
			const $item = this.el.items[i];
			const itemCenterX = $item.offsetLeft + $item.offsetWidth / 2;
			const itemDistanceFromCenter = this.coords.container.width / 2 - (itemCenterX + this.coords.slides.position.lerped.x);
			if (!$itemClosest || Math.abs(itemDistanceFromCenter) < Math.abs(distanceClosest)) {
				$itemClosest = $item;
				distanceClosest = itemDistanceFromCenter;
				indexClosest = i;
			}
		}

		multiClassList(this.el.items, 'remove', 'is-active');
		$itemClosest.classList.add('is-active');

		this.states.index = indexClosest;

		// dispatch event

		this.el.container.dispatchEvent(new CustomEvent(SliderDraggableEvent.CHANGE_ACTIVE, { detail: { index: this.states.index, x: this.coords.slides.position.lerped.x, ratio: this.states.ratio } }));
	}

	// determine cursor drag

	checkMouseDown() {
		if (!this.timeouts.drag) {
			this.timeouts.drag = setTimeout(() => {
				this.startDrag();
			}, this.config.delays.dragStart);
		}
	}

	checkMouseUp() {
		this.stopDrag();
	}

	// enable / disable dragging

	startDrag() {
		this.clearDragTimeout();
		this.el.container.classList.add('is-mouse-down');

		if (this.flags.isEnabled && !this.flags.isDragging) {
			// state flags
			this.flags.isDragging = true;
			this.flags.isDragComplete = true;
			this.flags.isSeeking = false;

			// save starting coordinates
			this.coords.cursor.start.x = CursorController.x;
			this.coords.cursor.start.lerped.x = CursorController.lerpX;
			this.coords.slides.start.x = this.coords.slides.position.x;
			this.coords.slides.start.lerped.dx = 0;
			this.coords.slides.start.lerped.x = this.coords.slides.position.lerped.x;

			// render state change
			this.el.container.classList.add('is-dragging');

			// dispatch drag event
			this.el.container.dispatchEvent(new CustomEvent(SliderDraggableEvent.DRAG_START));
		}
	}

	stopDrag() {
		this.clearDragTimeout();
		this.el.container.classList.remove('is-mouse-down');

		if (this.flags.isDragging) {
			this.flags.isDragging = false;
			this.flags.isDragComplete = false;

			// render state change
			this.el.container.classList.remove('is-dragging');

			// dispatch drag event
			this.el.container.dispatchEvent(new CustomEvent(SliderDraggableEvent.DRAG_END));

			if (this.config.isFullWidth) {

				// check distance from starting coords to current coords to determine snapping
				const itemWidth = this.el.items[0].scrollWidth;
				const dragDistanceX = this.coords.cursor.start.x - CursorController.x;
				const dragItemRatio = dragDistanceX / itemWidth;

				let slideIndex = this.states.index;

				if (Math.abs(dragItemRatio) >= this.config.snapping.itemSnapRatio) {
					if (dragItemRatio > 0) {
						slideIndex++;
					} else {
						slideIndex--;
					}
				}

				this.seek(slideIndex);

			} else {
				this.flick(CursorController.lerpVX);
			}
		}
	}

	clearDragTimeout() {
		clearTimeout(this.timeouts.drag);
		this.timeouts.drag = null;
	}

	// measure all (via update)

	tick() {
		this.measure();
		this.render();
	}

	measure() {
		this.measureResize(true);
		this.measureDrag(true);
	}

	render() {
		this.renderResize();
		this.renderDrag();
	}

	// cursor

	measureDrag(forceRender = false) {
		let isRendering = forceRender;

		// calculate if user cursor is within parent wrapper boundaries

		const inX = (CursorController.x >= this.coords.container.left && CursorController.x <= this.coords.container.right);
		const inY = (CursorController.y >= this.coords.container.top && CursorController.y <= this.coords.container.bottom);

		this.flags.isMouseOver = (inX && inY);

		if (this.flags.isEnabled) {

			this.coords.cursor.last.x = this.coords.cursor.position.x;
			this.coords.cursor.last.lerped.x = this.coords.cursor.position.lerped.x;

			this.coords.cursor.position.x = CursorController.x;
			this.coords.cursor.position.lerped.x = CursorController.lerpX;

			if (this.flags.isDragging) {

				// if dragging, lerp the slide position based on the cursor

				this.coords.slides.position.x += (this.coords.cursor.position.x - this.coords.cursor.last.x);
				this.coords.slides.position.lerped.dx = (this.coords.slides.position.x - this.coords.slides.position.lerped.x) * CursorController.config.lerp.ratio;
				this.coords.slides.position.lerped.x += this.coords.slides.position.lerped.dx;

				this.coords.slides.position.x = clamp(this.coords.slides.position.x, 0 - this.coords.itemsWrapper.width + this.coords.container.width, 0);
				this.coords.slides.position.lerped.x = clamp(this.coords.slides.position.lerped.x, 0 - this.coords.itemsWrapper.width + this.coords.container.width, 0);

				isRendering = true;

			} else if (!this.flags.isDragComplete && this.flags.isSeeking) {

				// if not dragging, tween the slides to the nearest snap position

				const dxLast = this.coords.slides.position.lerped.dx;

				this.coords.slides.position.x = this.coords.slides.destination.x;
				this.coords.slides.position.lerped.dx = (this.coords.slides.position.x - this.coords.slides.position.lerped.x) * this.config.snapping.lerp.ratio;

				if (this.coords.slides.position.lerped.dx) {
					this.coords.slides.position.lerped.x += this.coords.slides.position.lerped.dx * ((dxLast / this.coords.slides.position.lerped.dx) ** 2);
				}

				// TODO: fix lerp here

				if (Math.abs(this.coords.slides.destination.x - this.coords.slides.position.lerped.x) <= this.config.snapping.lerp.roundOff) {
					this.coords.slides.position.lerped.x = this.coords.slides.position.x;
					this.flags.isDragComplete = true;
					this.flags.isSeeking = false;
				}

				isRendering = true;

			} else if (!this.flags.isFlickComplete) {

				const lerpRatio = this.config.flicking.lerp.ratio;
				const lerpRoundOff = this.config.flicking.lerp.roundOff;

				this.coords.slides.position.x += this.coords.flick.vx;
				this.coords.slides.position.lerped.dx = (this.coords.slides.position.x - this.coords.slides.position.lerped.x) * lerpRatio;
				this.coords.slides.position.lerped.x += this.coords.slides.position.lerped.dx;

				// clamp slides to stay on screen

				this.coords.slides.position.x = clamp(this.coords.slides.position.x, 0 - this.coords.itemsWrapper.width + this.coords.container.width, 0);
				this.coords.slides.position.lerped.x = clamp(this.coords.slides.position.lerped.x, 0 - this.coords.itemsWrapper.width + this.coords.container.width, 0);

				// update velocity lerp

				this.coords.flick.vx += (0 - this.coords.flick.vx) * lerpRatio;

				// check to see if flick is complete

				if (this.flags.isFlickCompletePending) {
					this.flickComplete();
				} else if (Math.abs(this.coords.flick.vx) < lerpRoundOff) {
					this.flags.isFlickCompletePending = true;
					this.coords.flick.vx = 0;
					this.coords.slides.position.x = Math.round(this.coords.slides.position.x);
					this.coords.slides.position.lerped.x = this.coords.slides.position.x;
				}

				isRendering = true;
			}

			this.states.ratio = Math.abs(this.coords.slides.position.x / (this.coords.itemsWrapper.width - this.coords.container.width));
		}

		return isRendering;
	}

	renderDrag() {
		if (this.flags.isEnabled) {
			this.el.itemsWrapper.style.transform = `translateX(${this.coords.slides.position.lerped.x}px)`;
			this.el.container.dispatchEvent(new CustomEvent(SliderDraggableEvent.SCROLL, { detail: { x: this.coords.slides.position.lerped.x, ratio: this.states.ratio } }));
		}
	}

	// resize

	measureResize(forceRender = false) {
		this.coords.container = getRootOffsetBounds(this.el.container);
		this.coords.itemsWrapper = getRootOffsetBounds(this.el.itemsWrapper);

		// store position calculations for animation use

		if (this.flags.isSlider) {
			this.coords.slides.boundaries.left = this.coords.container.left + this.config.snapping.left;
			this.coords.slides.boundaries.right = this.coords.container.right - this.config.snapping.right;
		}

		this.coords.slides.boundaries.top = this.coords.container.top + this.config.snapping.top;
		this.coords.slides.boundaries.bottom = this.coords.container.bottom - this.config.snapping.bottom;

		return forceRender || ResizeController.isPlatformChange || ResizeController.isViewportChange;
	}

	renderResize() {
		if (ResizeController.isViewportChange && this.config.isFullWidth) {
			this.seek(this.states.index, true);
		}

		if (!this.config.isMobileEnabled && ResizeController.platform === Platform.MOBILE) {
			this.disable();
		} else {
			this.enable();
		}
	}

	//

	disable() {
		if (this.flags.isEnabled) {
			this.flags.isEnabled = false;
			AnimationController.clear(this.controllers.animation);
			this.removeDragStyles();
		}
	}

	enable() {
		if (!this.flags.isEnabled) {
			this.flags.isEnabled = true;
			this.controllers.animation = AnimationController.set({
				update: this.#onAnimationUpdate,
				render: this.#onAnimationRender
			});
		}
	}

	//

	removeDragStyles() {
		this.el.itemsWrapper.style.transform = '';
	}

	//

	destroy() {
		this.#removeEventListeners();
	}

}
